import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Lock, Calendar, Users, MessageSquare, Trophy, DollarSign, Shield, BarChart3 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { usePageRefresh } from '../../contexts/RefreshContext';
import { useSquads } from '../hooks/useSquads';
import SquadDetailsProvider from '../services/SquadDetailsProvider';
import { btn } from '../../utils/buttonStyles';
import { SPORT_TYPES } from '../../scoring/scoringConfig';
import SquadSummaryHandler from '../components/SquadSummaryHandler';
import PostsTab from '../tabs/PostsTab';
import MembersTab from '../tabs/MembersTab';
import ChatTab from '../tabs/ChatTab';
import ScheduleTab from '../tabs/ScheduleTab';
import CurrentEventTab from '../tabs/CurrentEventTab';
import PastGamesTab from '../tabs/PastGamesTab';
import GameDetailTab from '../tabs/GameDetailTab';
import ExpenseTab from '../tabs/ExpenseTab';
import TeamsTab from '../tabs/TeamsTab';
import StatsTab from '../tabs/StatsTab';
import Tabs from '../../components/common/Tabs';

/**
 * SquadDetailsPage - Clean, modular replacement for GroupDetailPage
 *
 * Architecture:
 * - This page is ONLY a shell for routing, tabs, and provider management
 * - NO business logic for any specific feature
 * - All tab components are self-contained and receive voProvider prop
 * - SquadDetailsProvider (voProvider) is the single source of truth
 */

const VALID_TABS = ['posts', 'members', 'chat', 'schedule', 'current-event', 'past-games', 'expenses', 'teams', 'statistics', 'game-view'];

const SquadDetailsPage = () => {
  const { squadId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { theme } = useTheme();
  const { isAuthenticated } = useAuth();
  const { displayGroupFeeds: featureDisplayGroupFeeds } = useFeatureFlags();
  const { setBreadcrumbItems } = useBreadcrumb();

  // Pull-to-refresh (mobile app has no address-bar reload)
  usePageRefresh(() => window.location.reload());

  // Get group API functions (only need these for provider initialization)
  const { getGroup, getGroupMembers } = useSquads(false);

  // Provider instance (create once, persist across renders)
  const voProviderRef = useRef(null);
  const [providerReady, setProviderReady] = useState(false);
  const [initError, setInitError] = useState(null);

  // Tab state
  const [activeTab, setActiveTab] = useState(() => getInitialTab());

  // Chat unread count (placeholder for now)
  const [chatUnreadCount, setChatUnreadCount] = useState(0);

  // Pre-filled post content (for sharing game results)

  // Dynamic game-view tab — set when navigating from My Games to a specific past game
  const [dynamicGameTab, setDynamicGameTab] = useState(null); // { gameId, eventId, label }

  // Get squad data (needed for conditional rendering)
  const squad = voProviderRef.current?.getGroup();

  /**
   * Get initial tab from URL or localStorage
   */
  function getInitialTab() {
    const urlTab = searchParams.get('tab');
    if (urlTab && VALID_TABS.includes(urlTab)) {
      return urlTab;
    }

    const savedTab = localStorage.getItem(`squadDetailTab-${squadId}`);
    if (savedTab && VALID_TABS.includes(savedTab)) {
      return savedTab;
    }

    return 'posts'; // Default tab
  }

  /**
   * Initialize provider when component mounts or squadId changes
   */
  useEffect(() => {
    const initProvider = async () => {
      try {
        setProviderReady(false);
        setInitError(null);

        // Create provider if doesn't exist
        if (!voProviderRef.current || voProviderRef.current.vo.groupId !== squadId) {
          voProviderRef.current = new SquadDetailsProvider(squadId);

          // Pass searchParams directly (provider will parse it)
          const hooks = { getGroup, getGroupMembers };
          await voProviderRef.current.initialize(searchParams, hooks);
          setProviderReady(true);
        }
      } catch (error) {
        console.error('[SquadDetailsPage] Provider initialization failed:', error);
        setInitError(error.message || 'Failed to load squad');
        setProviderReady(false);
      }
    };

    if (squadId && isAuthenticated) {
      initProvider();
    }
  }, [squadId, isAuthenticated]);

  /**
   * Set breadcrumb items when squad data is available
   */
  useEffect(() => {
    if (providerReady && voProviderRef.current) {
      const squadData = voProviderRef.current.getGroup();
      if (squadData) {
        setBreadcrumbItems([
          { label: 'Home', path: '/' },
          { label: 'Squads', path: '/squads' },
          { label: squadData.name || 'Squad' }
        ]);
      }
    }
    return () => {
      // Clear breadcrumb when component unmounts
      setBreadcrumbItems([]);
    };
  }, [providerReady, setBreadcrumbItems]);

  /**
   * Handle tab changes
   * Updates: active tab state, localStorage, URL params
   */
  const handleTabChange = (tab) => {
    if (!VALID_TABS.includes(tab)) {
      console.warn(`[SquadDetailsPage] Invalid tab: ${tab}`);
      return;
    }

    setActiveTab(tab);

    // Save to localStorage
    localStorage.setItem(`squadDetailTab-${squadId}`, tab);

    // Update URL (replace so back button doesn't go through tab history)
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    setSearchParams(newParams, { replace: true });
  };

  /**
   * Close the dynamic game-view tab and go back to past-games
   */
  const handleCloseDynamicTab = () => {
    setDynamicGameTab(null);
    handleTabChange('past-games');
  };

  /**
   * Watch for external URL changes (browser back/forward, deep links, My Games navigation)
   */
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    if (urlTab && VALID_TABS.includes(urlTab) && urlTab !== activeTab) {
      setActiveTab(urlTab);
      localStorage.setItem(`squadDetailTab-${squadId}`, urlTab);
    }

    // If the provider is already initialised for THIS squad (same-squad navigation,
    // e.g. clicking a game in My Games), tell it about the new eventId / gameId so
    // it loads the correct event instead of staying on whatever was loaded before.
    const provider = voProviderRef.current;
    if (provider && provider.isInitialized() && provider.vo.groupId === squadId) {
      provider.handleUrlChange(searchParams);
    }
  }, [searchParams, squadId]);

  /**
   * Create / update the dynamic game-view tab when URL contains tab=game-view + gameId
   */
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    const gameId = searchParams.get('gameId');
    const eventId = searchParams.get('eventId');

    if (urlTab === 'game-view' && gameId && eventId) {
      setDynamicGameTab(prev => {
        // Only recreate if game changed (avoids unnecessary re-renders)
        if (prev?.gameId === parseInt(gameId) && prev?.eventId === parseInt(eventId)) return prev;
        return { gameId: parseInt(gameId), eventId: parseInt(eventId), label: 'Game View' };
      });
    }
  }, [searchParams]);

  /**
   * Redirect from Teams tab if sport is Poker
   */
  useEffect(() => {
    if (providerReady && squad) {
      const sportNameLower = squad?.sportType?.sportName?.toLowerCase();
      const isPoker = sportNameLower === SPORT_TYPES.POKER;

      // If current tab is teams and sport is poker, redirect to posts
      if (activeTab === 'teams' && isPoker) {
        handleTabChange('posts');
      }
    }
  }, [providerReady, squad, activeTab]);

  // Loading state
  if (!providerReady && !initError) {
    return (
      <div className={`min-h-screen ${theme.background} flex items-center justify-center`}>
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Error state
  if (initError) {
    return (
      <div className={`min-h-screen ${theme.background} flex items-center justify-center`}>
        <div className="text-center">
          <h2 className={`text-2xl font-bold ${theme.text} mb-2`}>Squad Not Found</h2>
          <p className={`${theme.textSecondary} mb-4`}>
            {initError || 'The squad you\'re looking for doesn\'t exist or you don\'t have access to it.'}
          </p>
          <button
            onClick={() => navigate('/squads')}
            className={`${btn.base} ${btn.lg} ${theme.buttonPrimary}`}
          >
            Back to Squads
          </button>
        </div>
      </div>
    );
  }

  // Determine if Teams tab should be shown (hide for Poker)
  const sportName = squad?.sportType?.sportName?.toLowerCase();
  const showTeamsTab = sportName !== SPORT_TYPES.POKER;

  return (
    <div className={`${theme.background}`}>
      {/* Open Graph Meta Tags */}
      <Helmet>
        <title>{squad?.name || 'Squad'} | Kynaix Sports</title>
        <meta name="description" content={squad?.description || `Join ${squad?.name} on Kynaix Sports!`} />
        <meta property="og:title" content={squad?.name} />
        <meta property="og:description" content={squad?.description || `Join ${squad?.name} on Kynaix Sports!`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:site_name" content="Kynaix Sports" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 lg:px-0 py-6">
        {/* Squad Header - Self-Contained Component */}
        <SquadSummaryHandler voProvider={voProviderRef.current} />

        {/* Navigation Tabs - Only visible to members */}
        {squad?.isMember ? (
          <>
            <Tabs
              tabs={[
                ...(featureDisplayGroupFeeds ? [{ id: 'posts', label: 'Posts', icon: MessageSquare }] : []),
                { id: 'members', label: 'Members', icon: Users },
                {
                  id: 'chat',
                  label: 'Chat',
                  icon: MessageSquare,
                  badge: chatUnreadCount > 0 ? chatUnreadCount : null
                },
                { id: 'schedule', label: 'Schedule', icon: Calendar },
                { id: 'current-event', label: 'Current Event', shortLabel: 'Event', icon: Trophy },
                { id: 'past-games', label: 'Past Games', shortLabel: 'Games', icon: Trophy },
                { id: 'expenses', label: 'Expenses', icon: DollarSign },
                { id: 'statistics', label: 'Statistics', shortLabel: 'Stats', icon: BarChart3 },
                ...(showTeamsTab ? [{ id: 'teams', label: 'Teams', icon: Shield }] : []),
                // Dynamic tab — only shown when navigating to a specific past game
                ...(dynamicGameTab ? [{
                  id: 'game-view',
                  label: dynamicGameTab.label,
                  shortLabel: 'Game',
                  icon: Trophy,
                  onClose: handleCloseDynamicTab
                }] : [])
              ]}
              activeTab={activeTab}
              onTabChange={handleTabChange}
              mobileVisibleTabs={['posts', 'members', 'current-event']}
              className="mb-6"
            />

            {/* Tab Content */}
            <div>
              {featureDisplayGroupFeeds && activeTab === 'posts' && <PostsTab voProvider={voProviderRef.current} />}

              {activeTab === 'members' && <MembersTab voProvider={voProviderRef.current} />}

              {activeTab === 'chat' && <ChatTab voProvider={voProviderRef.current} />}

              {activeTab === 'schedule' && <ScheduleTab voProvider={voProviderRef.current} />}

              {activeTab === 'current-event' && <CurrentEventTab voProvider={voProviderRef.current} />}

              {activeTab === 'past-games' && <PastGamesTab voProvider={voProviderRef.current} />}

              {activeTab === 'expenses' && <ExpenseTab voProvider={voProviderRef.current} />}
              {activeTab === 'statistics' && <StatsTab voProvider={voProviderRef.current} />}

              {activeTab === 'teams' && showTeamsTab && <TeamsTab voProvider={voProviderRef.current} />}

              {activeTab === 'game-view' && dynamicGameTab && (
                <GameDetailTab
                  voProvider={voProviderRef.current}
                  gameCommonId={dynamicGameTab.gameId}
                  eventId={dynamicGameTab.eventId}
                  onClose={handleCloseDynamicTab}
                />
              )}
            </div>
          </>
        ) : (
          /* Non-member view */
          <div className={`${theme.card} rounded-lg p-12 text-center`}>
            <Lock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className={`text-2xl font-semibold ${theme.text} mb-2`}>Join to See More</h2>
            <p className={`${theme.textMuted} mb-6`}>
              This squad is {squad?.privacy === 'private' ? 'private' : 'public'}.
              {squad?.privacy === 'private'
                ? squad?.memberStatus === 'invited'
                  ? ' You have been invited! Click Accept Invitation to join.'
                  : ' You need to be invited or request to join.'
                : ' Click Join Squad to become a member.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SquadDetailsPage;
